import styled from '@emotion/styled';
import { Input, List, Select } from 'antd';
import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { useTranslate } from '@tolgee/react';
import { useMemoizedFn, useUpdateEffect } from 'ahooks';
import { useStoreNameConfig } from 'hooks/business/store/useStoreName';
import toLowerCase, { toUpperCase } from 'utils/toLowerCase';
import { StoreInfo } from 'services/api/store';
import { platformLabelMap } from 'services/config/platform';
import useFollowList from './hooks/useFollowList';
import {
  usePlatformListOfRegionByRegionsMap,
  useRealRegionListByRegionsMap,
  useRegionMap,
} from './hooks/useRegionList';
import useStoreList from './hooks/useStoreList';
import useStoreSelector from './hooks/useStoreSelector';
import LoadMore from './LoadMore';
import StoreListFooter from './StoreList/Footer';
import StoreListHeader from './StoreList/Header';
import StoreListItem from './StoreList/Item';
import LeftTab from './LeftTab';
import { StoreSelectorMainProps, StoreSelectorValue } from './interface';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import { get } from 'lodash-es';

const StoreSelectorContainer = styled.div`
  display: flex;
  width: 461px;
  max-height: 440px;
  height: 440px;
  background-color: #ffffff;
  border: 1px solid rgba(225, 228, 235, 1);
  border-radius: 2px;
  box-shadow: 0 2px 12px 0 rgba(192, 195, 204, 0.15);
`;

const Left = styled.div`
  width: 120px;
  flex: none;
  background-color: #f7f9fa;
  .platform-select {
    .ant-select-selector {
      padding-left: 16px;
      padding-right: 16px;
      height: 40px;
      line-height: 40px;
      font-weight: 500;
      color: #515359;
      display: flex;
      align-items: center;
      box-shadow: inset 0 -1px 0 0 rgba(240, 242, 245, 1) !important;
    }
    .ant-select-selection-placeholder {
      color: #515359;
    }
    .ant-select-selection-item {
      text-transform: capitalize;
    }
  }
`;

const Right = styled.div`
  display: flex;
  flex-direction: column;
  .search-box {
    padding: 6px 9px;
    box-shadow: inset 0 -1px 0 0 rgba(240, 242, 245, 1);
    width: 336px;
  }
  .ant-list {
    overflow: auto;
    flex: 1;
    width: 336px;
    overflow-x: hidden;
    margin-top: 8px;
  }
`;
const StoreSelectorMain = React.memo((props: StoreSelectorMainProps) => {
  const {
    className,
    onChange,
    value,
    onClose,
    platformLimit,
    displayUnreadCount,
    scene,
    visible,
    quickSelectDisable,
    mutilple = true,
    whiteStoreList,
  } = props;
  const { t } = useTranslate();

  const { whiteListConfig: { AE_CONVERSATION = false } = {} } = useOrgConfigStateValue();

  const { loading: regionMapLoading, data: regionsMap } = useRegionMap({ scene });
  const platformListSource = usePlatformListOfRegionByRegionsMap(regionsMap);
  const platformList = useMemo(
    () =>
      platformListSource.filter((platform) => {
        if (platform === 'ALIEXPRESS' && !AE_CONVERSATION) {
          return false;
        }

        return true;
      }),
    [platformListSource, AE_CONVERSATION],
  );
  let singlePlatform: General.PlatformUpperCaseTunnel | undefined;

  if (platformLimit || platformList.length === 1) {
    singlePlatform = platformLimit || platformList[0];
  }

  const params = useMemo(() => {
    return {
      region: value?.region,
      platform: platformLimit || value?.platform || singlePlatform,
      scene,
    };
  }, [value?.region, value?.platform, platformLimit, singlePlatform, scene]);

  const [refreshKey, setRefreshKey] = useState(0);

  useUpdateEffect(() => {
    if (visible) {
      setRefreshKey((key) => key + 1);
    }
  }, [visible]);

  const {
    loadMore,
    loading,
    isLoadingMore,
    storeList: _storeList,
    onChange: setSearchOption,
    searchOptions,
    hasMore,
    setStoreList,
  } = useStoreList(params, { ready: !regionMapLoading, refreshKey });
  const {
    currentPlatformFollowList,
    onChangeFollowStatus,
    onClearAllFollow,
    isFollowed,
    allFollowList,
  } = useFollowList(searchOptions.platform);
  const regionList = useRealRegionListByRegionsMap(
    regionsMap,
    searchOptions.platform || params.platform,
  );

  const [activeTab, changeActiveTab] = useState<'all' | 'follower' | General.UpperCaseRegion>(
    'all',
  );
  const {
    selected,
    onChange: onSelectStore,
    isSelected,
  } = useStoreSelector(value?.storeList, mutilple);
  const { config } = useStoreNameConfig();
  const setActiveTab = useCallback(
    (activeTab: 'all' | 'follower' | General.UpperCaseRegion) => {
      changeActiveTab(activeTab);
      if (activeTab === 'follower') {
        setSearchOption((pre) => ({
          ...pre,
          region: undefined,
          follower: '1',
        }));
      } else if (activeTab === 'all') {
        setSearchOption((pre) => ({
          ...pre,
          region: undefined,
          follower: undefined,
        }));
      } else {
        setSearchOption((pre) => ({
          ...pre,
          region: activeTab,
          follower: undefined,
        }));
      }
    },
    [setSearchOption, changeActiveTab],
  );

  const clearAllFollow = useCallback(() => {
    onClearAllFollow();
    setActiveTab('all');
  }, [onClearAllFollow, setActiveTab]);
  const onFollowChange = useCallback(
    (item: StoreInfo, status: boolean) => {
      onChangeFollowStatus(item, status).then(() => {
        if (!status) {
          const next = _storeList.filter((x) => x.storeId !== item.storeId);
          setStoreList((pre) => {
            return {
              next: pre?.next || '',
              hasMore: !!pre?.hasMore,
              list: next,
            };
          });
          if (!next.length) {
            setActiveTab('all');
          }
        }
      });
    },
    [onChangeFollowStatus, setActiveTab, setStoreList, _storeList],
  );
  const onSubmit = useMemoizedFn((value: StoreSelectorValue) => {
    onChange?.(value);
    onClose?.();
  });

  const storeList = useMemo(() => {
    const list =
      whiteStoreList && Array.isArray(whiteStoreList)
        ? _storeList.filter((item) => {
            return whiteStoreList.includes(item.storeId);
          })
        : _storeList;

    return list;
  }, [_storeList, whiteStoreList]);

  const preStoreListLength = useRef(0);
  useEffect(() => {
    if (
      get(whiteStoreList, 'length', 0) > 0 &&
      hasMore &&
      preStoreListLength.current === storeList.length
    ) {
      loadMore();
    }
    preStoreListLength.current = storeList.length;
  }, [whiteStoreList, storeList, hasMore, loadMore]);
  useEffect(() => {
    preStoreListLength.current = 0;
  }, [activeTab]);
  useUpdateEffect(() => {
    preStoreListLength.current = 0;
  }, [visible]);

  return (
    <StoreSelectorContainer className={className}>
      <Left>
        {singlePlatform ? (
          <div className="platform-select">
            <div className="ant-select-selector">
              {platformLabelMap[toLowerCase(singlePlatform)]}
            </div>
          </div>
        ) : (
          <Select
            className="platform-select"
            bordered={false}
            value={toLowerCase(searchOptions.platform)}
            onChange={(value: General.PlatformTunnel) => {
              const hasFollowed = allFollowList.some((x) => {
                if (value) {
                  return x.externalType.toLowerCase() === value;
                }
                return true;
              });
              if (!value) {
                if (hasFollowed) {
                  setActiveTab('follower');
                }
                setSearchOption((pre) => ({
                  ...pre,
                  platform: undefined,
                }));

                return;
              }
              if (!regionsMap[value]?.includes(activeTab as any)) {
                setActiveTab(hasFollowed ? 'follower' : 'all');
                hasFollowed &&
                  setSearchOption((pre) => ({
                    ...pre,
                    region: undefined,
                  }));
              } else if (activeTab === 'follower' && !hasFollowed) {
                setActiveTab('all');
              }
              setSearchOption((pre) => ({
                ...pre,
                platform: toUpperCase(value),
              }));
            }}
            style={{ width: 120 }}
            placeholder={t(
              'scene.chatWorkspace.conversationList.storeSelector.platform.placeholder',
            )}
            allowClear
            filterOption
            options={platformList.map((x) => ({
              value: toLowerCase(x),
              label: platformLabelMap[toLowerCase(x)],
            }))}
          />
        )}

        <LeftTab
          regionList={regionList}
          onChange={setSearchOption}
          followList={currentPlatformFollowList}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          platform={singlePlatform ?? searchOptions.platform}
          onSubmit={onSubmit}
          quickSelectDisable={quickSelectDisable}
        />
      </Left>
      <Right className="store-selector-right">
        <div className="search-box">
          <Input.Search
            onSearch={(value) =>
              setSearchOption((pre) => ({
                ...pre,
                keyword: value,
              }))
            }
            placeholder={t(
              'scene.chatWorkspace.conversationList.storeSelector.placeholder.searchStore',
            )}
            enterButton
            allowClear
          />
        </div>
        {mutilple && storeList.length > 0 ? (
          <StoreListHeader
            onClearAllFollow={clearAllFollow}
            selected={selected}
            currentTabStoreList={storeList}
            onChange={onSelectStore}
            currentTab={activeTab}
            followList={currentPlatformFollowList}
            platform={searchOptions.platform}
          />
        ) : null}

        <List
          dataSource={storeList}
          rowKey="storeId"
          loading={loading && !isLoadingMore}
          locale={{
            emptyText: t('storeSelector.暂无数据'),
          }}
          renderItem={(item) => (
            <StoreListItem
              item={item}
              onFollowChange={onFollowChange}
              onSelect={onSelectStore}
              isSelected={isSelected}
              isFollowed={isFollowed}
              storeNameConfig={config}
              onSubmit={(item) => onSubmit?.({ storeList: item })}
              highlightWords={searchOptions.keyword}
              displayUnreadCount={displayUnreadCount}
            />
          )}
          split={false}
          footer={
            storeList.length ? (
              <LoadMore loadMore={loadMore} hasMore={activeTab === 'follower' ? false : hasMore} />
            ) : null
          }
        />
        <StoreListFooter selected={selected} onSubmit={(item) => onSubmit?.({ storeList: item })} />
      </Right>
    </StoreSelectorContainer>
  );
});

export default StoreSelectorMain;
